import React, { useEffect, useState } from "react";
import moment from "moment";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import Indicator from "../../../../components/base/Indicator";
import Icon from "../../../../components/base/Icon";
import { toShortDateWithoutTime, useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import "./PlanChange.css";
import "./AddonsCard.css";


const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })


// "Pause my account" -- the hold the plan document offers in both the downgrade and the
// cancellation flow, before the confirm button.
//
// $29 a month for up to three months, starting when the period already paid for ends. Data and
// phone numbers are kept and everyone can still sign in and look, but nothing can be changed or
// sent. The customer is reminded every week that it is paused and still being charged, Rinvox
// telephones as it runs out, and if it is not resumed by then the account moves to the Free plan.
export default function PauseDrawer(props: {
    drawerRef?: React.RefObject<DrawerFormRef>,
    onClose: () => void,
    onPaused: () => void
}) {

    const hub = useHub()

    const [preview, previewSet] = useState<any>()
    const [loading, loadingSet] = useState<boolean>(true)
    const [months, monthsSet] = useState<number>(1)

    useEffect(() => {
        hub.Get("/accounts/profile/plan/hold/preview", res => {
            loadingSet(false)
            if (res.success) previewSet(res.data)
            else toastError(res.message ?? "Your account cannot be paused.")
        }, () => loadingSet(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const starts = toShortDateWithoutTime(preview?.startsAt)
    const until = preview ? toShortDateWithoutTime(moment(preview.startsAt).add(months, "months").toDate()) : ""
    const max = preview?.maxMonths ?? 3

    const pause = (callback: any) => {
        hub.Post("/accounts/profile/plan/hold/schedule", { months }, res => {
            callback(res)
            if (res.success) {
                toastSuccess(`Your account will be paused from ${starts} until ${until}.`)
                props.onPaused()
            }
            else toastError(res.message ?? "Couldn't pause your account.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-plan-pause"
            ref={props.drawerRef}
            title="Pause my account"
            titleIcon="pause"
            subtitle="Keep everything, pay less, come back when you are ready"
            size="medium"
            buttonText={preview ? "Pause my account from " + starts : "Pause my account"}
            confirmDisabled={!preview}
            onClose={props.onClose}
            onSubmit={(body, callback) => pause(callback)}>

            {
                loading ?
                    <div className="py-20"><Indicator /></div>
                    : !preview ?
                        <div className="text-gray-600 py-10 text-center">Your account cannot be paused right now.</div>
                        :
                        <div className="plan-change">

                            <div className="plan-change-hero mb-8">
                                <div className="plan-change-hero-price">
                                    {money.format(preview.price)}<span>/month</span>
                                </div>
                                <div className="fs-7 text-gray-700">
                                    instead of {money.format(preview.planPrice)} for {preview.planName}, for up to {max} months
                                </div>
                            </div>

                            <div className="fw-bold mb-3">How long?</div>
                            <div className="extra-users-picker plan-change-months mb-3">
                                {
                                    Array.from({ length: max }, (_, i) => i + 1).map(n =>
                                        <button key={n} type="button"
                                            className={"extra-users-option" + (n === months ? " is-selected" : "")}
                                            onClick={e => { e.preventDefault(); monthsSet(n); }}>
                                            {n} month{n === 1 ? "" : "s"}
                                        </button>
                                    )
                                }
                            </div>

                            <div className="plan-change-summary mb-8">
                                <div className="plan-change-row">
                                    <span>Starts</span>
                                    <b>{starts} — when your paid period ends</b>
                                </div>
                                <div className="plan-change-row">
                                    <span>Paused until</span>
                                    <b>{until}</b>
                                </div>
                                <div className="plan-change-row">
                                    <span>Costs</span>
                                    <b>{money.format(preview.price)} a month</b>
                                </div>
                            </div>

                            <div className="plan-change-section-title">While it is paused</div>
                            <ul className="plan-change-list mb-8">
                                <li><Icon icon="check" type="solid" size={11} />Your data and phone numbers are kept.</li>
                                <li><Icon icon="check" type="solid" size={11} />Everyone can still sign in and look at everything.</li>
                                <li><Icon icon="lock" type="solid" size={11} />Nothing can be changed or sent — no new jobs, messages or outgoing calls. Incoming calls still reach you.</li>
                                <li><Icon icon="bell" type="solid" size={11} />We remind you every week that it is paused and still being charged.</li>
                                <li><Icon icon="phone" type="solid" size={11} />Near the end, somebody from Rinvox calls you to see whether you are coming back.</li>
                                <li><Icon icon="play" type="solid" size={11} />Resume any time: {preview.planName} starts again that day at its full price.</li>
                                <li><Icon icon="circle-exclamation" type="solid" size={11} />If you have not resumed by {until}, the account moves to the Free plan.</li>
                            </ul>

                            <div className="fs-8 text-gray-500">
                                Until {starts} nothing changes, and you can cancel the pause from your subscription page.
                            </div>

                        </div>
            }

        </DrawerForm>
    )
}
